import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Loader2, Star, Truck, Package, Clock, IndianRupee } from "lucide-react";

/**
 * Courier comparison modal — rendered as a nested shadcn Dialog so Radix correctly
 * stacks it above the parent booking Dialog without auto-closing it.
 *
 * Props:
 *  - open, onClose
 *  - pickup, delivery, weight
 *  - ownPrice — the platform's own calculated price (replaces PickNGo Direct's slab-based rate)
 *  - onSelect({courier_id, courier_name, courier_price, estimated_days, reliability})
 */
export default function CouriersCompareModal({ open, onClose, pickup, delivery, weight, onSelect, ownPrice }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("best_value");
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (!pickup || !delivery) return;
    setLoading(true);
    setError(null);
    setSelectedId(null);
    api
      .post("/couriers/compare", {
        pickup_address: pickup,
        delivery_address: delivery,
        weight_kg: weight || 1,
      })
      .then(({ data }) => {
        if (ownPrice && data?.options?.length) {
          // Realistic scaling: PickNGo Direct uses our own price; other couriers
          // are 10-15% more expensive to reflect vendor premium.
          const MARKUP = {
            xpressbees: 1.10,
            ecom: 1.11,
            delhivery: 1.12,
            dtdc: 1.13,
            bluedart: 1.15,
          };
          const base = Math.round(ownPrice);
          data.options = data.options.map((o) => {
            if (o.id === "pickngo_direct") return { ...o, price: base };
            const factor = MARKUP[o.id] ?? 1.12;
            return { ...o, price: Math.round(base * factor) };
          });
          data.options.sort((a, b) => a.price - b.price);
        }
        setData(data);
        if (!data?.options?.length) setError("No couriers available for this route right now.");
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || err.message || "Failed to load rates";
        setError(String(msg));
        toast.error(`Failed to load rates: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, [open, pickup, delivery, weight, ownPrice]);

  const sorted = () => {
    const opts = [...(data?.options || [])];
    if (sort === "fastest") opts.sort((a, b) => a.estimated_days - b.estimated_days || a.price - b.price);
    else if (sort === "most_reliable") opts.sort((a, b) => b.reliability - a.reliability || a.price - b.price);
    else opts.sort((a, b) => a.price - b.price);
    return opts;
  };

  const confirm = () => {
    if (!selectedId) { toast.error("Select a courier first"); return; }
    const opt = (data?.options || []).find((o) => o.id === selectedId);
    if (!opt) return;
    onSelect({
      courier_id: opt.id,
      courier_name: opt.name,
      courier_price: opt.price,
      estimated_days: opt.estimated_days,
      reliability: opt.reliability,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="courier-compare-modal">
        <DialogHeader className="p-5 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
            <Truck className="h-5 w-5 text-[#FF6B00]" /> Compare Couriers
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5 text-left">Pick the best option — selecting one updates your booking price.</p>
        </DialogHeader>

        <div className="flex gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/60 shrink-0">
          {[
            { k: "best_value", l: "Best Value", icon: IndianRupee },
            { k: "fastest", l: "Fastest", icon: Clock },
            { k: "most_reliable", l: "Most Reliable", icon: Star },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setSort(t.k)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition ${sort === t.k ? "bg-[#FF6B00] text-white" : "bg-white border border-slate-200 text-slate-600"}`}
              data-testid={`sort-${t.k}`}
            >
              <t.icon className="h-3 w-3" /> {t.l}
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading && <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>}
          {!loading && error && (
            <div className="p-6 text-center space-y-2" data-testid="compare-error-state">
              <p className="text-sm font-semibold text-red-600">{error}</p>
              <p className="text-xs text-slate-500">Please check the pickup / delivery addresses and try again.</p>
            </div>
          )}
          {!loading && !error && data && (
            <>
              <p className="text-xs text-slate-500 mb-3">Distance: <span className="font-semibold">{data.distance_km} km</span> · Weight: <span className="font-semibold">{data.weight_kg} kg</span></p>
              <div className="space-y-2">
                {sorted().map((opt, i) => (
                  <button
                    key={opt.id}
                    onClick={() => setSelectedId(opt.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition ${selectedId === opt.id ? "border-[#FF6B00] bg-orange-50/60" : "border-slate-200 hover:border-orange-200"}`}
                    data-testid={`courier-option-${opt.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {opt.id === "pickngo_direct"
                        ? <div className="h-10 w-10 rounded-lg bg-[#FF6B00] flex items-center justify-center text-white"><Truck className="h-5 w-5" /></div>
                        : opt.logo
                          ? <img src={opt.logo} alt={opt.name} className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-100" onError={(e) => { e.target.style.display = "none"; }} />
                          : <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><Package className="h-5 w-5 text-slate-400" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-['Outfit'] font-bold text-slate-900">{opt.name}</p>
                          {i === 0 && sort === "best_value" && <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">CHEAPEST</span>}
                          {opt.id === "pickngo_direct" && <span className="text-[9px] font-bold bg-[#FF6B00]/10 text-[#FF6B00] px-1.5 py-0.5 rounded">OUR QUOTE · OWN NETWORK</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{opt.tagline}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1.5">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {opt.estimated_days} day{opt.estimated_days > 1 ? "s" : ""}</span>
                          <span className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-500" /> {opt.reliability}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-['Outfit'] text-xl font-bold text-slate-900">₹{opt.price}</p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">all inclusive</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex gap-2 shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl" data-testid="compare-cancel-btn">Cancel</Button>
          <Button onClick={confirm} disabled={!selectedId} className="flex-1 bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl" data-testid="courier-select-confirm">
            Select &amp; Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
